var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { action, computed, makeObservable, observable, runInAction, } from 'mobx';
import { CartService, } from '@data/api/services/cart';
import { Accommodation, Addon } from '@data/models';
import ShoppingCartItem, { buildLinkingId, isAccommodationShoppingCartItem, isAddonShoppingCartItem, } from '@data/models/ShoppingCartItem';
import MultilangText from '@data/models/MultilangText';
import { eventSystem } from '@business/event-system';
import TimeUtils from '@business/utils/time';
import { isVariableChargeType } from '@data/models/ChargeType';
import { inventory } from '@business/inventory';
import { FeatureFlag, FeatureFlagManager } from '@business/feature-flags';
import ShoppingCartTravelInsuranceItem from '@data/models/ShoppingCartTravelInsuranceItem';
import Store from '../Store';
import { persistentPropertyStorage } from '../PersistentPropertyStorage';
export default class Cart {
    constructor() {
        Object.defineProperty(this, "isLoadingTotals", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "items", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "totals", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "travelInsurance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "findAccommodation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (accommodation, unit) => this.items.find(({ model }) => isAccommodationShoppingCartItem(model) &&
                model.item.id === accommodation.id &&
                (!unit || model.unit === unit))
        });
        Object.defineProperty(this, "findAccommodationByUUID", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (accommodationUUID) => {
                return this.items.find(({ model }) => {
                    const isInCart = isAccommodationShoppingCartItem(model);
                    const { adults, kids, unit } = model;
                    const { id: roomId, packageId } = model.item;
                    const uuid = buildLinkingId({
                        adults,
                        kids,
                        packageId,
                        roomId,
                        unit,
                    });
                    return isInCart && uuid === accommodationUUID;
                });
            }
        });
        Object.defineProperty(this, "findAddonByIdAndAccommodationId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (addonId, accommodationUUID) => this.items.find((item) => this.matchAddonByIdAndAccomodationId(addonId, item, accommodationUUID))
        });
        Object.defineProperty(this, "findAddonsByAccommodationId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (accommodationUUID) => this.items.filter(({ model }) => isAddonShoppingCartItem(model) &&
                model.accommodationUUID === accommodationUUID)
        });
        Object.defineProperty(this, "findAddonsForRoom", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (shoppingCartItem) => this.items.filter(({ model }) => isAddonShoppingCartItem(model) &&
                model.accommodationUUID === shoppingCartItem.id)
        });
        Object.defineProperty(this, "findAllAccomodations", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (accommodation) => this.items.filter(({ model }) => isAccommodationShoppingCartItem(model) &&
                model.item.id === accommodation.id)
        });
        Object.defineProperty(this, "findItem", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (item) => this.items.find((i) => item.getItemId() === i.getItemId() &&
                item.model.type === i.model.type &&
                item.getLinkingId() === i.getLinkingId())
        });
        Object.defineProperty(this, "findLinkedAddons", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (item) => this.items.reduce((acc, i) => {
                if (isAddonShoppingCartItem(i.model) &&
                    i.getLinkingId() === item.getLinkingId()) {
                    acc.push(i.model);
                }
                return acc;
            }, [])
        });
        Object.defineProperty(this, "findRoomForAddon", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (linkingId) => this.getAssociatedShoppingCartItems(linkingId, 'accommodation')[0]
        });
        Object.defineProperty(this, "getAddonsForEachAccommodation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const addonsByAccommodationId = this.items
                    .filter(({ model }) => isAccommodationShoppingCartItem(model))
                    .reduce((acc, { id }) => {
                    acc[id] = [];
                    return acc;
                }, {});
                this.addons.forEach((addon) => {
                    addonsByAccommodationId[addon.accommodationUUID || ''].push(addon);
                });
                return addonsByAccommodationId;
            }
        });
        Object.defineProperty(this, "getAmountMultiplierForAddon", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (addon, accommodationItem = this.findRoomForAddon(buildLinkingId(addon))) => isVariableChargeType(addon) && accommodationItem
                ? accommodationItem.model.amount
                : 1
        });
        Object.defineProperty(this, "getAssociatedShoppingCartItems", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (linkingId, searchType) => this.items.filter((item) => { var _a; return ((_a = item.model) === null || _a === void 0 ? void 0 : _a.type) == searchType && item.getLinkingId() == linkingId; })
        });
        Object.defineProperty(this, "matchAddonByIdAndAccomodationId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (addonId, item, accommodationUUID) => isAddonShoppingCartItem(item.model) &&
                addonId === item.getItemId() &&
                accommodationUUID === item.model.accommodationUUID
        });
        makeObservable(this);
    }
    get storageKey() {
        return 'cart';
    }
    add(item) {
        this.updateInsurance(null, true);
        const existingItem = this.findItem(item);
        if (existingItem) {
            existingItem.model.amount += item.model.amount;
        }
        else {
            this.items.push(item);
        }
        this.items = [...this.items];
        return { isNew: !existingItem, item };
    }
    addAccommodation({ accommodation, amount = 1, adults = 1, kids = 0, unit, reservationId, associatedSplitInventoryUnits = [], }) {
        if (amount < 1)
            return;
        const useNewSplitInventorySystem = FeatureFlagManager.isEnabled(FeatureFlag.useNewSplitInventorySystem);
        const { isNew, item } = this.add(new ShoppingCartItem({
            adults,
            amount,
            associatedSplitInventoryUnits: useNewSplitInventorySystem
                ? []
                : associatedSplitInventoryUnits,
            item: accommodation,
            kids,
            reservationId,
            type: 'accommodation',
            unit,
        }));
        if (!isNew) {
            this.findLinkedAddons(item).forEach((model) => {
                if (isVariableChargeType(model.item)) {
                    model.amount += item.model.amount;
                }
            });
        }
        this.handleUpdateItems();
        this.emitAddToCartEvent(item);
    }
    addAddon({ addon, amount = 1, reservationId, uuid, accommodationUUID, }) {
        if (amount < 1)
            return;
        const accommodation = this.findAccommodationByUUID(accommodationUUID);
        if (!accommodation)
            return;
        const { item } = this.add(new ShoppingCartItem({
            accommodationUUID,
            amount: amount * this.getAmountMultiplierForAddon(addon, accommodation),
            item: addon,
            reservationId,
            type: 'addon',
            unit: accommodation.model.unit,
        }, uuid));
        this.handleUpdateItems();
        this.emitAddToCartEvent(item);
    }
    calculate() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const unpurchasedItems = this.items.filter((x) => !x.isAlreadyPurchased);
            if (unpurchasedItems.length === 0) {
                return;
            }
            if (this.property && this.currency && this.checkIn && this.checkOut) {
                runInAction(() => {
                    this.isLoadingTotals = true;
                    this.totals = null;
                });
                const accommodations = this.accommodations.map((x) => ({
                    adults: x.adults,
                    bookedId: x.item.bookedId,
                    kids: x.kids,
                    name: x.item.name,
                    packageId: x.item.packageId,
                    packageName: x.item.packageName,
                    quantity: x.amount,
                    rateId: x.item.rateId,
                    roomId: x.item.id,
                    smartPolicyId: x.item.smartPolicyRootId,
                    smartPolicyRootId: x.item.smartPolicyRootId,
                    unit: x.unit,
                }));
                const addons = this.addons.map((x) => ({
                    accommodationAdults: x.item.adults,
                    accommodationBookedId: x.item.accommodationBookedId,
                    accommodationId: x.item.roomId,
                    accommodationKids: x.item.kids,
                    accommodationPackageId: x.item.packageId,
                    addonId: x.item.id,
                    bookedId: x.item.bookedId,
                    chargeType: x.item.chargeType,
                    quantity: x.amount,
                }));
                const code = (_a = Store.components.searchPanel.value) === null || _a === void 0 ? void 0 : _a.code;
                const lang = Store.environment.language;
                // BENG-4165: only price an external offer (CFAR) when the property has
                // external offers enabled, so a stale cart selection never adds a premium
                // to the totals the guest pays.
                const externalOffers = this.property.externalOffersEnabled &&
                    this.travelInsurance &&
                    this.travelInsurance.offerId !== 'none'
                    ? [
                        {
                            offerId: this.travelInsurance.offerId,
                            provider: this.travelInsurance.provider,
                        },
                    ]
                    : [];
                try {
                    const totals = yield CartService.calculate({
                        accommodations,
                        addons,
                        checkIn: this.checkIn,
                        checkOut: this.checkOut,
                        code,
                        currency: this.currency,
                        externalOffers,
                        lang,
                        propertyId: this.property.id,
                    });
                    runInAction(() => {
                        this.totals = totals;
                    });
                }
                finally {
                    runInAction(() => {
                        this.isLoadingTotals = false;
                    });
                }
            }
        });
    }
    clear() {
        this.items = [];
        this.totals = null;
    }
    emitAddToCartEvent(item) {
        const property = Store.property.model;
        const { checkin, checkout, code } = Store.components.searchPanel.value;
        // For already purchased items, we don't want to track them
        if (!item.isAlreadyPurchased && property) {
            eventSystem.emit('internal/add-to-cart', {
                checkin: TimeUtils.toDateString(checkin),
                checkout: TimeUtils.toDateString(checkout),
                code: code || '',
                items: [item.model],
                property,
            });
        }
    }
    handleUpdateItems() {
        Store.savePersistentData();
        inventory.recalculate({ cartItems: this.accommodations });
    }
    load() {
        const data = persistentPropertyStorage.getItem(this.storageKey);
        if (data) {
            const parsed = JSON.parse(data);
            this.items = parsed.items;
            for (let i = 0; i < this.items.length; i++) {
                const item = this.items[i];
                if (isAddonShoppingCartItem(item.model) && item.model.item) {
                    item.model.item.name = new MultilangText(item.model.item.name);
                    item.model.item.description = new MultilangText(item.model.item.description);
                }
                this.items[i] = new ShoppingCartItem(item.model, item.id);
            }
            this.totals = parsed.totals;
        }
    }
    remove(item) {
        if (!item || !item.model)
            return;
        this.updateInsurance(null, true);
        this.items = [...this.items].filter((x) => x.id !== item.id);
        this.handleUpdateItems();
        const property = Store.property.model;
        const { checkin, checkout, code } = Store.components.searchPanel.value;
        property &&
            eventSystem.emit('internal/remove-from-cart', {
                checkin: TimeUtils.toDateString(checkin),
                checkout: TimeUtils.toDateString(checkout),
                code: code || '',
                items: [item.model],
                property,
            });
        if (isAccommodationShoppingCartItem(item.model)) {
            this.findAddonsForRoom(item).forEach((addonItem) => {
                this.remove(addonItem);
            });
        }
    }
    removeAccommodation(accommodation, unit) {
        this.remove(this.findAccommodation(accommodation, unit));
    }
    removeAddon(item, accommodationUUID) {
        const list = this.items.filter((i) => {
            if (isAccommodationShoppingCartItem(i.model) ||
                i.model.item.bookedId.length > 0) {
                return false;
            }
            if (item.chargeType === 'per-night' ||
                item.chargeType === 'per-reservation') {
                return i.getItemId() === item.id;
            }
            return (i.getItemId() === item.id &&
                i.model.accommodationUUID === accommodationUUID);
        });
        for (const item of list) {
            this.remove(item);
        }
    }
    removeByCartId(id) {
        this.remove(this.items.find((item) => item.id == id));
    }
    save() {
        persistentPropertyStorage.setItem(this.storageKey, JSON.stringify(this));
    }
    update(addon, amount, accommodationUUID) {
        this.items = [...this.items].map((item) => {
            if (this.matchAddonByIdAndAccomodationId(addon.id, item, accommodationUUID) &&
                item.model) {
                item.model.amount = amount;
            }
            return item;
        });
        this.handleUpdateItems();
    }
    updateAddon(addon, amount = 1, accommodationUUID) {
        if (amount < 1)
            return;
        this.update(addon, amount, accommodationUUID);
    }
    updateInsurance(data, avoidCalculate = false) {
        let shouldCalculate = false;
        if (this.travelInsurance || ((data === null || data === void 0 ? void 0 : data.offerId) && data.offerId !== 'none')) {
            shouldCalculate = true;
        }
        if (!data) {
            this.travelInsurance = null;
        }
        else {
            this.travelInsurance = new ShoppingCartTravelInsuranceItem({
                offerId: data.offerId,
                premium: data.premium,
                provider: data.provider || 'hts',
                selectionId: data.selectionId,
            });
        }
        Store.savePersistentData();
        if (shouldCalculate && !avoidCalculate) {
            this.calculate();
        }
    }
    // Used by the Confirmation page only
    updateTotals(data_res) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.property || this.addons.length === 0) {
                return;
            }
            runInAction(() => {
                this.isLoadingTotals = true;
            });
            const addons = [];
            for (const addon of this.addons) {
                // skip already purchased addons
                if (addon.item.bookedId.length > 0)
                    continue;
                const accommodation = this.accommodations.find((item) => item.item.id == addon.item.roomId &&
                    item.item.packageId === addon.item.packageId &&
                    item.adults === addon.item.adults &&
                    item.kids === addon.item.kids);
                if (accommodation) {
                    const accommodationBookedId = addon.item.accommodationBookedId;
                    addons.push({
                        accommodationBookedId,
                        accommodationId: accommodation.item.id,
                        addonId: addon.item.id,
                        bookedId: [],
                        chargeType: addon.item.chargeType,
                        quantity: addon.amount,
                    });
                }
            }
            try {
                yield CartService.updateTotals({
                    addons,
                    cartItems: this.items,
                    data_res,
                    propertyId: this.property.id,
                });
                // Clear cart as the itens were purchased
                this.clear();
            }
            finally {
                runInAction(() => {
                    this.isLoadingTotals = false;
                });
            }
        });
    }
    get accommodations() {
        return this.items
            .map(({ model }) => model)
            .filter((model) => isAccommodationShoppingCartItem(model));
    }
    get addons() {
        return this.items
            .map(({ model }) => model)
            .filter((model) => isAddonShoppingCartItem(model));
    }
    get property() {
        return Store.property.model;
    }
    get currency() {
        return Store.environment.currency;
    }
    get numberOfNights() {
        if (this.accommodations.length > 0) {
            return this.accommodations[0].item.nights;
        }
        else {
            return undefined;
        }
    }
    get checkIn() {
        return Store.components.searchPanel.value.checkin;
    }
    get checkOut() {
        return Store.components.searchPanel.value.checkout;
    }
    // Ids of split inventory for accommodations already added to the cart - should not be added again
    get prohibitedSplitInventory() {
        const splitInvs = [];
        this.accommodations.forEach((accommodation) => {
            splitInvs.push(...accommodation.associatedSplitInventoryUnits);
        });
        return splitInvs;
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], Cart.prototype, "isLoadingTotals", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], Cart.prototype, "items", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Cart.prototype, "totals", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Cart.prototype, "travelInsurance", void 0);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Cart.prototype, "addAccommodation", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Cart.prototype, "addAddon", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Cart.prototype, "clear", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [ShoppingCartItem]),
    __metadata("design:returntype", void 0)
], Cart.prototype, "remove", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Accommodation, String]),
    __metadata("design:returntype", void 0)
], Cart.prototype, "removeAccommodation", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Addon, Object]),
    __metadata("design:returntype", void 0)
], Cart.prototype, "removeAddon", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], Cart.prototype, "removeByCartId", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Addon, Object, Object]),
    __metadata("design:returntype", void 0)
], Cart.prototype, "updateAddon", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Object]),
    __metadata("design:returntype", void 0)
], Cart.prototype, "updateInsurance", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], Cart.prototype, "accommodations", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], Cart.prototype, "addons", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Cart.prototype, "property", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Cart.prototype, "currency", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Cart.prototype, "numberOfNights", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Cart.prototype, "checkIn", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Cart.prototype, "checkOut", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Cart.prototype, "prohibitedSplitInventory", null);
